import './_import.scss';
import './_import.dark-mode.scss';
import React, { useEffect, useReducer } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import PropTypes from 'prop-types';

import Steps from './Steps';

import * as importTypes from './reducer/constants';
import { importReducer, importInitState } from './reducer';
import { serviceServicesGetImportServices, serviceServicesImport, serviceServicesImportCheck } from '../../../../../../services/endpoints/services';
import { delay } from '../../../../../../utils/helper';
import { getErrorMessage } from '../../../helpers/getters';
import { getCategoriesList } from '../../../../../../redux/actions/services';
import { importServicesSuccess } from '../../../reducer';

const ModalImportServices = ({ serviceDispatch, onClose, servicesCount }) => {
  const dispatch = useDispatch();

  const [importState, dispatchImportState] = useReducer(importReducer, importInitState);

  const { config } = useSelector((state) => state.services);
  const { providerId, isLoading, listSelected, options, listDefault } = importState;

  const getProviderForm = async (providerId) => {
    try {
      const response = await serviceServicesGetImportServices(providerId);
      if (response?.data) {
        const { data } = response;
        await delay(500);
        return data;
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      await delay(500);
      return Promise.reject(errorMessage);
    }
  };

  /**
   *
   * @param taskId
   * @description
   * Статусы:
   * - waiting
   * - success
   * - fail
   * @returns {Promise<JSX.Element|*>}
   */
  const importServicesCheck = async (taskId) => {
    try {
      const delayCount = config?.import_request_interval ? config.import_request_interval * 1000 : 1000;
      const response = await serviceServicesImportCheck(taskId);
      if (response?.data) {
        const status = response?.data?.status;
        switch (status) {
        case 'waiting':
          await delay(delayCount);
          return importServicesCheck(taskId);
        case 'success':
          return true;
        case 'fail':
          return new Error('Service check fail');
        default:
          await delay(delayCount);
          return importServicesCheck(taskId);
        }
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      await delay(500);
      return Promise.reject(errorMessage);
    }
  };

  const importServices = async () => {
    try {
      let cloneList = _.cloneDeep(listSelected);
      if (!options.support_copy_descriptions) {
        cloneList = cloneList?.map((category) => ({
          ...category,
          services: category?.services?.map((service) => {
            const findDefaultService = listDefault
              ?.find((cat) => cat.provider_category_id === category.provider_category_id)
              ?.services?.find((s) => s.id === service?.id);
            const newService = {
              ...service,
              formData: _.pickBy(
                {
                  ...service.formData,
                  description: findDefaultService?.description === service.formData?.description ? null : service.formData?.description,
                },
                (value) => value !== null,
              ),
            };
            return newService;
          }),
        }));
      }
      const response = await serviceServicesImport(providerId, cloneList, options);
      if (response?.data) {
        const taskId = response?.data?.id;
        await delay(500);
        return taskId;
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      await delay(500);
      return Promise.reject(errorMessage);
    }
  };

  const onSubmit = () => {
    dispatchImportState({ type: importTypes.IMPORT_SUBMIT });
    importServices()
      .then((taskId) => importServicesCheck(taskId)
        .then(() => {
          dispatchImportState({ type: importTypes.IMPORT_SUBMIT_SUCCESS });
          return delay(1000);
        })
        .then(() => dispatch(getCategoriesList()))
        .then(() => serviceDispatch(importServicesSuccess([1]))))
      .catch((error) => {
        dispatchImportState({ type: importTypes.IMPORT_SUBMIT_ERROR, error });
      });
  };

  useEffect(() => {
    if (providerId) {
      getProviderForm(providerId)
        .then((data) => {
          const list = data?.categories;
          const options = data?.options;
          dispatchImportState({ type: importTypes.IMPORT_CHANGE_PROVIDER_FORM_SUCCESS, list, options });
        })
        .catch((errorMessage) => {
          dispatchImportState({ type: importTypes.IMPORT_CHANGE_PROVIDER_FORM_ERROR, error: errorMessage });
        });
    }
  }, [providerId]);

  return (
    <>
      <Modal.Header closeButton={!isLoading}>
        <Modal.Title>Import services</Modal.Title>
      </Modal.Header>
      <div className="services-import">
        <Steps onClose={onClose} onSubmit={onSubmit} dispatchImportState={dispatchImportState} servicesCount={servicesCount} {...importState} />
      </div>
    </>
  );
};

ModalImportServices.propTypes = {
  modalData: PropTypes.any,
  serviceDispatch: PropTypes.func,
  onClose: PropTypes.func,
  servicesCount: PropTypes.number,
};

export default ModalImportServices;
